import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { API_BASE_URL } from "./utils/api";
import LandingPage from "./pages/LandingPage";
import ChatPage from "./pages/ChatPage";
import SectionMapperPage from "./pages/SectionMapperPage";
import CalculatorPage from "./pages/CalculatorPage";
import NoticePage from "./pages/NoticePage";
import DonationsPage from "./pages/DonationsPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import AdminPortalPage from "./pages/AdminPortalPage";
import PricingPage from "./pages/PricingPage";
import PlansPage from "./pages/PlansPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import RefundPage from "./pages/RefundPage";
import ContactPage from "./pages/ContactPage";
import LabourLawPage from "./pages/LabourLawPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import StaffPortalPage from "./pages/StaffPortalPage";
import AdvanceTaxPlannerPage from "./pages/AdvanceTaxPlannerPage";
import TaxAuditClause20bPage from "./pages/TaxAuditClause20bPage";
import PulsePage from "./pages/PulsePage";
import AdminBlogPage from "./pages/AdminBlogPage";
import WhatsAppFAB from "./components/WhatsAppFAB";
import "./App.css";

// Inject analytics and SEO dynamically (keeps view-source clean)
const injectAnalytics = () => {
  // Google Analytics
  if (!document.querySelector('script[src*="googletagmanager"]')) {
    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-VLBYDCKRG5';
    document.head.appendChild(gtagScript);
    
    const gtagInit = document.createElement('script');
    gtagInit.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-VLBYDCKRG5');
    `;
    document.head.appendChild(gtagInit);
  }
  
  // PostHog Analytics
  if (!window.posthog) {
    const phScript = document.createElement('script');
    phScript.textContent = `
      !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?(u=e[a]=[]):a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once unregister identify setPersonProperties reset get_distinct_id".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
      posthog.init('phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE',{api_host:'https://us.i.posthog.com',person_profiles:'identified_only'});
    `;
    document.head.appendChild(phScript);
  }
  
  // SEO Schema (JSON-LD) - injected dynamically
  if (!document.querySelector('script[type="application/ld+json"]')) {
    const schemaOrg = document.createElement('script');
    schemaOrg.type = 'application/ld+json';
    schemaOrg.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "TaxSetu AI",
      "description": "TaxSetu AI - India's #1 AI-powered Tax Expert",
      "url": "https://taxsetu.ai",
      "applicationCategory": "FinanceApplication",
      "operatingSystem": "Web Browser",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" }
    });
    document.head.appendChild(schemaOrg);
  }
};

function App() {
  // Track user visit for analytics
  useEffect(() => {
    // Inject analytics dynamically
    injectAnalytics();
    
    const trackVisit = async () => {
      try {
        await fetch(`${API_BASE_URL}/api/track-visit`, { method: 'POST' });
      } catch (e) {
        // Silent fail
      }
    };
    
    // Track on load
    trackVisit();
    
    // Track every 2 minutes while user is active
    const interval = setInterval(trackVisit, 120000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <div className="App noise-overlay">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/section-mapper" element={<SectionMapperPage />} />
              <Route path="/calculator" element={<CalculatorPage />} />
              <Route path="/advance-tax" element={<AdvanceTaxPlannerPage />} />
              <Route path="/tax-audit/clause-20b" element={<TaxAuditClause20bPage />} />
              <Route path="/pulse/:slug" element={<PulsePage />} />
              <Route path="/admin/blog" element={<AdminBlogPage />} />
              <Route path="/notice" element={<NoticePage />} />
              <Route path="/donations" element={<DonationsPage />} />
              <Route path="/auth/google" element={<AuthCallbackPage />} />
              <Route path="/admin" element={<AdminPortalPage />} />
              <Route path="/staff" element={<StaffPortalPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/plans" element={<PlansPage />} />
              <Route path="/labour-law" element={<LabourLawPage />} />
              {/* Policy Pages - Required for Razorpay */}
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/refund" element={<RefundPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
            </Routes>
          </BrowserRouter>
          <WhatsAppFAB />
          <Toaster position="top-right" />
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
